if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        if (this.form) {
          this.form.querySelector("[name=id]").disabled = false;
        }
        // Listen on the custom element so submit still works after variant AJAX replaces the <form>.
        this.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.minicart = document.querySelector("mini-cart");
        this.quickViewDrawer = this.closest("quick-view-drawer");
        this.eventContext = this.form?.dataset?.eventContext;
        this.hideErrors = this.dataset.hideErrors === "true";

        const submitBtn = this.querySelector('[type="submit"]');
        const labelSpan = submitBtn?.querySelector("span");
        if (submitBtn && labelSpan && !submitBtn.dataset.originalAddToCartText) {
          submitBtn.dataset.originalAddToCartText = labelSpan.textContent.trim();
        }
      }

      parseQty(raw) {
        if (typeof parseQuantityInputValue === "function") {
          return parseQuantityInputValue(raw);
        }
        const digits = String(raw ?? "").replace(/[^\d]/g, "");
        if (digits === "") return NaN;
        const n = parseInt(digits, 10);
        return Number.isFinite(n) ? n : NaN;
      }

      /** Visible quantity field → plain integer string (PLP hidden field + PDP quantity-input). */
      resolveQuantityForCart(form) {
        const hiddenQty = form.querySelector(".form-quantity-input");
        if (hiddenQty) {
          const scope = this.closest(
            ".product-row-table, .product-row, li.grid-item, product-card",
          );
          const vis =
            scope?.querySelector(".quantity-input-simple .quantity-input-field") ||
            (hiddenQty.dataset.productId
              ? document.querySelector(
                  `.quantity-input-simple[data-product-id="${hiddenQty.dataset.productId}"] .quantity-input-field`,
                )
              : null);
          if (vis) {
            const n = this.parseQty(vis.value);
            if (!Number.isNaN(n) && n >= 1) return String(n);
          }
        }

        const fieldInForm =
          form.querySelector('.quantity-input-field[name="quantity"]') ||
          form.querySelector('[name="quantity"].quantity-input-field') ||
          form.querySelector(".quantity-input-field");

        if (fieldInForm) {
          const n = this.parseQty(fieldInForm.value);
          if (!Number.isNaN(n) && n >= 1) return String(n);
        }

        const formDataQty = new FormData(form).get("quantity");
        if (formDataQty != null && formDataQty !== "") {
          const n = this.parseQty(formDataQty);
          if (!Number.isNaN(n) && n >= 1) return String(n);
        }

        return null;
      }

      formatCartAddError(data, httpStatus, requestedQty) {
        const qty =
          typeof requestedQty === "number"
            ? requestedQty
            : parseInt(String(requestedQty ?? ""), 10);
        const platformMsg =
          typeof window.swQuantityOverPlatformMaxMessage === "function"
            ? window.swQuantityOverPlatformMaxMessage(qty)
            : "";

        const errorsRaw = data?.errors;
        let raw = "";
        if (typeof errorsRaw === "string" && errorsRaw.trim()) {
          raw = errorsRaw.trim();
        } else if (Array.isArray(errorsRaw)) {
          raw = errorsRaw.filter(Boolean).join(", ");
        } else if (errorsRaw && typeof errorsRaw === "object") {
          raw = Object.values(errorsRaw).flat().filter(Boolean).join(", ");
        }

        const desc = typeof data?.description === "string" ? data.description.trim() : "";
        const msg = typeof data?.message === "string" ? data.message.trim() : "";

        if (platformMsg) {
          const generic =
            (!raw && !desc && !msg) ||
            [raw, desc, msg].every((s) => !s || /^cart error$/i.test(s));
          if (generic) return platformMsg;
        }

        if (raw && !/^cart error$/i.test(raw)) return raw;
        if (desc && !/^cart error$/i.test(desc)) return desc;
        if (msg && !/^cart error$/i.test(msg)) return msg;

        return platformMsg || window.cartStrings?.error || "Could not add to cart.";
      }

      buildCartAddPayload(form, resolvedQty) {
        const variantId = parseInt(form.querySelector('[name="id"]')?.value, 10);
        const quantity = resolvedQty ? parseInt(resolvedQty, 10) : 1;
        if (!Number.isFinite(variantId) || !Number.isFinite(quantity) || quantity < 1) {
          return null;
        }

        const item = { id: variantId, quantity };
        const properties = {};

        for (const [key, value] of new FormData(form).entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) {
            properties[match[1]] = value;
          }
        }

        if (Object.keys(properties).length > 0) {
          item.properties = properties;
        }

        const sellingPlan = form.querySelector('[name="selling_plan"]')?.value;
        if (sellingPlan) {
          item.selling_plan = parseInt(sellingPlan, 10);
        }

        const payload = { items: [item] };

        if (this.minicart?.getSectionsToRender) {
          payload.sections = this.minicart
            .getSectionsToRender()
            .map((section) => section.section)
            .join(",");
        }
        payload.sections_url = window.location.pathname;

        return payload;
      }

      onSubmitHandler(event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || form.dataset.type !== "add-to-cart-form") {
          return;
        }

        event.preventDefault();
        this.form = form;
        this.eventContext = form.dataset.eventContext;
        form.querySelector("[name=id]").disabled = false;

        const submitButton = form.querySelector('[type="submit"]');
        const spinner = submitButton?.querySelector(".button-overlay-spinner");
        if (submitButton.classList.contains("loading")) {
          return;
        }

        const buttonText = submitButton.querySelector("span");
        const originalLabel =
          submitButton.dataset.originalAddToCartText ||
          (buttonText ? buttonText.textContent.trim() : "");
        if (
          submitButton.classList.contains("button-added") &&
          buttonText &&
          originalLabel
        ) {
          buttonText.textContent = originalLabel;
          submitButton.classList.remove("button-added");
        }

        this.handleErrorMessage();

        submitButton.setAttribute("aria-disabled", true);
        submitButton.classList.add("loading");
        spinner?.classList.remove("hidden");

        const resolvedQty = this.resolveQuantityForCart(form);
        const hiddenQty = form.querySelector(".form-quantity-input");
        if (hiddenQty && resolvedQty) {
          hiddenQty.value = resolvedQty;
        }

        const payload = this.buildCartAddPayload(form, resolvedQty);
        if (!payload) {
          this.handleErrorMessage(window.cartStrings?.error || "Could not add to cart.");
          submitButton.classList.remove("loading");
          submitButton.removeAttribute("aria-disabled");
          spinner?.classList.add("hidden");
          return;
        }

        const addUrl = routes.cart_add_url.endsWith(".js")
          ? routes.cart_add_url
          : `${routes.cart_add_url}.js`;

        fetch(addUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(payload),
        })
          .then(async (response) => {
            const data = await response.json();
            const hasError = !response.ok || data.status;

            if (hasError) {
              const errorText = this.formatCartAddError(
                data,
                response.status,
                payload.items[0]?.quantity,
              );

              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: String(payload.items[0].id),
                errors: data.errors || data.description,
                message: data.message,
              });

              this.handleErrorMessage(errorText);
              if (this.minicart && this.minicart.updateSectionContents) {
                this.minicart.updateSectionContents();
              }
              return;
            }

            // Update minicart if it exists
            if (this.minicart && this.minicart.renderContents) {
              this.minicart.renderContents(data);
            }

            if (window.themeSettings && window.themeSettings.redirectToCart) {
              location.href = window.routes.cart_url;
            } else {
              if (this.quickViewDrawer) {
                this.quickViewDrawer.close();
                // Immediately re-add the 'drawer-open' class to avoid
                // the drawer backdrop from flashing.
                document.body.classList.add("drawer-open");
                if (this.minicart && this.minicart.open) {
                  setTimeout(() => this.minicart.open(), 200);
                }
              } else {
                if (this.minicart && this.minicart.open) {
                  this.minicart.open(submitButton);
                }
              }
            }

            const successSpan = submitButton.querySelector("span");
            if (successSpan) {
              successSpan.textContent = "Added";
              submitButton.classList.add("button-added");
            }

            const event = new CustomEvent("cart:item-add", {
              detail: {
                items: [data],
                context: this.eventContext,
              },
            });
            document.dispatchEvent(event);

            publish(PUB_SUB_EVENTS.cartItemAdd, {
              source: this.eventContext,
              items: [data],
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submitButton.classList.remove("loading");
            submitButton.removeAttribute("aria-disabled");
            spinner?.classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form-error-message-wrapper");
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form-error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
